/**
 * Pages API — embed allowlist.
 *
 *   GET /v1/admin/schools/:schoolPublicId/pages/embed-allowlist  (school admin or super-admin)
 *   PUT /v1/admin/schools/:schoolPublicId/pages/embed-allowlist  (super-admin only)
 *
 * Hostnames stored as lowercase (no scheme, no path). A leading `*.` is
 * the only wildcard form supported and matches subdomains of the
 * remainder. Empty array is permitted (block all embeds).
 */

import { z } from 'zod'

const SchoolPublicIdParam = z.object({ schoolPublicId: z.string().min(1) })

/**
 * Single allowlist entry. Examples:
 *   - `youtube.com`
 *   - `www.youtube.com`
 *   - `*.youtube.com` (matches any subdomain of youtube.com)
 *
 * Rules:
 *   - lowercase only (clients should normalize)
 *   - no scheme prefix (`https://`)
 *   - no path / query / fragment
 *   - optional single leading `*.` wildcard
 */
const HOSTNAME_PATTERN = /^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/

export const EmbedAllowlistEntrySchema = z
  .string()
  .min(1)
  .max(253)
  .regex(HOSTNAME_PATTERN, 'Allowlist entry must be a lowercase hostname (optionally prefixed with "*.")')
export type EmbedAllowlistEntry = z.infer<typeof EmbedAllowlistEntrySchema>

export const EmbedAllowlistSchema = z.array(EmbedAllowlistEntrySchema)
export type EmbedAllowlist = z.infer<typeof EmbedAllowlistSchema>

export const GetEmbedAllowlistParamsSchema = SchoolPublicIdParam
export type GetEmbedAllowlistParams = z.infer<typeof GetEmbedAllowlistParamsSchema>

export const GetEmbedAllowlistResponseSchema = z.object({
  allowlist: EmbedAllowlistSchema,
  usingDefaults: z.boolean()
})
export type GetEmbedAllowlistResponse = z.infer<typeof GetEmbedAllowlistResponseSchema>

export const UpdateEmbedAllowlistParamsSchema = SchoolPublicIdParam
export type UpdateEmbedAllowlistParams = z.infer<typeof UpdateEmbedAllowlistParamsSchema>

export const UpdateEmbedAllowlistRequestSchema = z.object({
  allowlist: EmbedAllowlistSchema
})
export type UpdateEmbedAllowlistRequest = z.infer<typeof UpdateEmbedAllowlistRequestSchema>

export const UpdateEmbedAllowlistResponseSchema = GetEmbedAllowlistResponseSchema
export type UpdateEmbedAllowlistResponse = z.infer<typeof UpdateEmbedAllowlistResponseSchema>
